/**
 * Client-side, staff-only filters that hide events whose end date has passed.
 * They never touch `is_active`, so the club app is unaffected. Shared between
 * the Evenimente page (the "Ascunde evenimentele trecute" switch) and the
 * navbar event picker so both honour the same preference.
 */

/**
 * Drops events whose end date is strictly before today. Templates and events
 * with no usable date are always kept. `now` is injectable for deterministic
 * tests. Pure — no component state, safe to unit-test in isolation.
 */
export function filterOutPastEvents(events: any[], hidePast: boolean, now: Date = new Date()): any[] {
  if (!hidePast) {
    return events;
  }
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return events.filter((e) => {
    if (e?.is_template) {
      return true;
    }
    const ref = e?.date_end || e?.date_start;
    if (!ref) {
      return true;
    }
    const end = new Date(ref);
    if (isNaN(end.getTime())) {
      return true;
    }
    return end >= today;
  });
}

/**
 * The list of events to show in the navbar picker. Applies `filterOutPastEvents`
 * but never hides the currently-selected event, so a deliberately-selected past
 * event stays visible (and re-selectable) in the dropdown while the toggle is on.
 * Order is preserved. Pure.
 */
export function visibleEventsForPicker(
  events: any[],
  hidePast: boolean,
  selectedId: number | null | undefined,
  now: Date = new Date(),
): any[] {
  if (!hidePast) {
    return events;
  }
  const keptIds = new Set(filterOutPastEvents(events, true, now).map((e) => e?.id));
  return events.filter((e) => keptIds.has(e?.id) || (selectedId != null && e?.id === selectedId));
}
